import { useState, useEffect, useCallback } from 'react';
import './Toast.css';

// Singleton state — callable from anywhere (hooks, utils)
let addToastFn = null;
const activeMessages = new Set();

/**
 * Show a toast notification. Can be called from anywhere.
 * Deduplicates: same message won't stack.
 *
 * @param {string} message
 * @param {'error'|'warning'|'info'} type
 * @param {number} duration - ms (default 5000)
 */
export function showToast(message, type = 'error', duration = 5000) {
  if (activeMessages.has(message)) return;
  if (addToastFn) {
    addToastFn({ message, type, duration, id: Date.now() + Math.random() });
    activeMessages.add(message);
  }
}

export function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    setToasts(prev => [...prev, toast]);
  }, []);

  const removeToast = useCallback((id, message) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    activeMessages.delete(message);
  }, []);

  useEffect(() => {
    addToastFn = addToast;
    return () => { addToastFn = null; };
  }, [addToast]);

  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onRemove={removeToast}
        />
      ))}
    </div>
  );
}

function ToastItem({ toast, onRemove }) {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setExiting(true), toast.duration - 300);
    const removeTimer = setTimeout(() => onRemove(toast.id, toast.message), toast.duration);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [toast, onRemove]);

  return (
    <div className={`toast toast-${toast.type}${exiting ? ' toast-exit' : ''}`}>
      <span className="toast-message">{toast.message}</span>
      <button
        className="toast-close"
        onClick={() => onRemove(toast.id, toast.message)}
        aria-label="Close"
      >
        &times;
      </button>
    </div>
  );
}
